import * as React from 'react';
import { OnlineApplicationAppletContext } from '../OnlineApplicationAppletContext';
import type { ApplicationFormModelProgress } from './useApplicationFormProgress';

export enum Page {
    OrderDetails = 'OrderDetails',
    Login = 'Login',
    Registration = 'Registration',
    PaymentOptions = 'PaymentOptions',
    Payment = 'Payment',
    StartDate = 'StartDate',
    Confirmation = 'Confirmation',
}

export interface ApplicationFormFlow {
    activePage: Readonly<Page>;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    switchPage: (page: Page) => void;
    getNextPage: (activePage: Page) => Page;
    getPreviousPage: (activePage: Page) => Page;
}

export const useApplicationFormFlow = (
    initModelProgress: ApplicationFormModelProgress | undefined,
    defaultActivePage: Page,
    isPaymentSupportedByPayEx: boolean,
    isOrderPaid: boolean,
    isOrderFree: boolean
): Readonly<ApplicationFormFlow> => {
    const context = React.useContext(OnlineApplicationAppletContext);

    const getNextPage = (activePage: Page) => {
        switch (activePage) {
            case Page.OrderDetails:
                return context.isUserLoggedIn ? Page.Registration : Page.Login;
            case Page.Login:
                return Page.Registration;
            case Page.Registration:
                return Page.StartDate;
            case Page.StartDate:
                return Page.PaymentOptions;
            case Page.PaymentOptions: {
                if (isOrderFree) {
                    return Page.Confirmation;
                }

                return isPaymentSupportedByPayEx ? Page.Payment : Page.Confirmation;
            }
            case Page.Payment:
                return Page.Confirmation;
            default:
                throw new Error(`Unrecognized Page value: ${activePage}.`);
        }
    };

    const getPreviousPage = (activePage: Page) => {
        switch (activePage) {
            case Page.Confirmation: {
                if (isOrderFree) {
                    return Page.PaymentOptions;
                }

                return isPaymentSupportedByPayEx && !isOrderPaid ? Page.Payment : Page.PaymentOptions;
            }
            case Page.Payment:
                return Page.PaymentOptions;
            case Page.PaymentOptions:
                return Page.StartDate;
            case Page.StartDate:
                return Page.Registration;
            case Page.Registration:
                return context.isUserLoggedIn ? Page.OrderDetails : Page.Login;
            case Page.Login:
                return Page.OrderDetails;
            default:
                throw new Error(`Unrecognized Page value: ${activePage}.`);
        }
    };

    const [activePage, setActivePage] = React.useState(() => {
        const initActivePage = initModelProgress && initModelProgress.activePage;

        if (initActivePage) {
            return initActivePage === Page.Login && context.isUserLoggedIn
                ? getNextPage(initActivePage)
                : initActivePage;
        }

        return defaultActivePage;
    });

    const goToNextPage = () => {
        setActivePage(getNextPage(activePage));
    };

    const goToPreviousPage = () => {
        setActivePage(getPreviousPage(activePage));
    };

    const switchPage = (page: Page) => {
        setActivePage(page);
    };

    return {
        activePage,
        goToNextPage,
        goToPreviousPage,
        switchPage,
        getNextPage,
        getPreviousPage,
    };
};
